/*
 * A+ Content modules — behavior for sections/dynamic-aplus-*.
 * Custom elements re-run connectedCallback when the theme editor re-renders a
 * section, so no Shopify section events are needed.
 * Every A+ section includes this file, so it guards against running twice.
 */
(() => {
  if (window.aplusLoaded) return;
  window.aplusLoaded = true;

  const parseTime = (value) =>
    String(value || '')
      .split(':')
      .reduce((total, part) => total * 60 + (parseFloat(part) || 0), 0);

  /* ---------------------------------------------------------------------------
     Carousel: native scroll-snap track, arrow buttons, progress + counter
     --------------------------------------------------------------------------- */
  class AplusCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.current = this.querySelector('[data-current]');
      if (!this.track || !this.slides.length) return;

      this.onScroll = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
    }

    index() {
      const start = this.slides[0].offsetLeft;
      const step = this.slides[1] ? this.slides[1].offsetLeft - start : 1;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.track.scrollLeft >= maxScroll - 2) return this.slides.length - 1;
      return Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / step)));
    }

    go(direction) {
      const target = this.slides[Math.min(this.slides.length - 1, Math.max(0, this.index() + direction))];
      this.track.scrollTo({ left: target.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
    }

    update() {
      const index = this.index();
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.style.setProperty('--progress', (index + 1) / this.slides.length);
      if (this.current) this.current.textContent = String(index + 1).padStart(2, '0');
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll - 2;
    }
  }

  /* ---------------------------------------------------------------------------
     Video: poster + play button swap in the player; chapters seek
     --------------------------------------------------------------------------- */
  class AplusVideo extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-video-frame]');
      this.template = this.querySelector('template[data-video-template]');
      this.chapters = [...this.querySelectorAll('[data-chapter]')];
      this.segments = [...this.querySelectorAll('[data-segment]')];
      this.times = this.chapters.map((chapter) => parseTime(chapter.dataset.time));

      this.scenesEl = this.querySelector('[data-scenes]');
      if (this.scenesEl) {
        this.initScenes();
        return;
      }

      this.querySelector('[data-play]')?.addEventListener('click', () => this.play(0));
      this.chapters.forEach((chapter, i) => chapter.addEventListener('click', () => this.play(this.times[i])));
    }

    disconnectedCallback() {
      this.sceneObserver?.disconnect();
    }

    /* Scenes mode: chapter clips play in sequence while on screen */
    initScenes() {
      this.scenes = [...this.scenesEl.querySelectorAll('[data-scene]')];
      this.index = 0;
      this.visible = false;
      this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.scenes.forEach((video, i) => {
        video.muted = true;
        video.playsInline = true;
        if (i === 0) video.preload = 'auto';
        video.addEventListener('timeupdate', () => i === this.index && this.fillSegment(i, video));
        video.addEventListener('ended', () => i === this.index && this.goTo((i + 1) % this.scenes.length));
        video.addEventListener('playing', () => this.classList.add('is-playing'));
        ['play', 'pause'].forEach((type) => video.addEventListener(type, () => i === this.index && this.syncScene()));
      });

      this.chapters.forEach((chapter, i) => chapter.addEventListener('click', () => {
        this.userPaused = false;
        this.goTo(i);
      }));
      this.scenesEl.addEventListener('click', () => this.toggleScene());
      this.scenesEl.addEventListener('keydown', (event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        event.preventDefault();
        this.toggleScene();
      });

      this.sceneObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.updatePlayback();
      }, { threshold: 0.35 });
      this.sceneObserver.observe(this.scenesEl);
      this.goTo(0);
      this.syncScene();
    }

    goTo(i) {
      this.scenes[this.index]?.pause();
      this.index = i;
      this.scenes.forEach((video, n) => video.classList.toggle('is-active', n === i));
      this.chapters.forEach((chapter, n) => chapter.classList.toggle('is-active', n === i));
      this.segments.forEach((segment, n) => segment.style.setProperty('--fill', n < i ? 1 : 0));
      const video = this.scenes[i];
      video.preload = 'auto';
      video.currentTime = 0;
      this.scenes[(i + 1) % this.scenes.length].preload = 'auto';
      this.updatePlayback();
    }

    updatePlayback() {
      const video = this.scenes[this.index];
      if (this.visible && !this.userPaused) video.play().catch(() => {});
      else video.pause();
    }

    toggleScene() {
      this.userPaused = !this.scenes[this.index].paused;
      if (!this.userPaused) this.visible = true;
      this.updatePlayback();
    }

    syncScene() {
      const paused = this.scenes[this.index].paused;
      this.classList.toggle('is-paused', paused);
      this.scenesEl.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }

    fillSegment(i, video) {
      const fill = video.duration ? Math.min(1, video.currentTime / video.duration) : 0;
      this.segments[i]?.style.setProperty('--fill', fill);
    }

    play(seconds) {
      if (!this.template) return;

      if (!this.classList.contains('is-playing')) {
        this.frame.appendChild(this.template.content.cloneNode(true));
        this.frame.hidden = false;
        this.classList.add('is-playing');
        this.video = this.frame.querySelector('video');
        if (this.video) {
          this.video.muted = true;
          this.video.addEventListener('timeupdate', () => this.progress());
          ['play', 'pause', 'ended'].forEach((type) => this.video.addEventListener(type, () => this.syncState()));
          this.frame.addEventListener('click', () => this.toggle());
          this.frame.addEventListener('keydown', (event) => {
            if (event.key !== ' ' && event.key !== 'Enter') return;
            event.preventDefault();
            this.toggle();
          });
        } else {
          this.frame.removeAttribute('role');
          this.frame.removeAttribute('tabindex');
          this.frame.removeAttribute('aria-label');
        }
      }

      if (this.video) {
        this.video.currentTime = seconds;
        this.video.play().catch(() => {});
        this.frame.focus();
        return;
      }

      const iframe = this.frame.querySelector('iframe');
      if (iframe) iframe.src = this.embedUrl(iframe.dataset.src, seconds);
    }

    toggle() {
      if (!this.video) return;
      if (this.video.ended) this.video.currentTime = 0;
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    syncState() {
      const paused = this.video.paused;
      this.classList.toggle('is-paused', paused);
      this.frame.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }

    embedUrl(src, seconds) {
      const start = Math.floor(seconds);
      if (src.includes('vimeo')) return `${src}&autoplay=1#t=${start}s`;
      return `${src}&autoplay=1&start=${start}`;
    }

    progress() {
      const now = this.video.currentTime;
      const end = this.video.duration || 0;
      this.segments.forEach((segment, i) => {
        const from = this.times[i] || 0;
        const to = this.times[i + 1] ?? end;
        const fill = to > from ? Math.min(1, Math.max(0, (now - from) / (to - from))) : 0;
        segment.style.setProperty('--fill', fill);
        this.chapters[i]?.classList.toggle('is-active', now >= from && now < to);
      });
    }
  }

  /* ---------------------------------------------------------------------------
     Comparison: mobile chips choose which alternative column is shown
     --------------------------------------------------------------------------- */
  class AplusCompare extends HTMLElement {
    connectedCallback() {
      this.chips = [...this.querySelectorAll('[data-chip]')];
      this.chips.forEach((chip) => chip.addEventListener('click', () => this.select(chip.dataset.chip)));
    }

    select(column) {
      this.chips.forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.chip === column)));
      this.querySelectorAll('[data-alt]').forEach((cell) => {
        cell.classList.toggle('is-active', cell.dataset.alt === column);
      });
    }
  }

  if (!customElements.get('aplus-carousel')) customElements.define('aplus-carousel', AplusCarousel);
  if (!customElements.get('aplus-video')) customElements.define('aplus-video', AplusVideo);
  if (!customElements.get('aplus-compare')) customElements.define('aplus-compare', AplusCompare);

  /* ---------------------------------------------------------------------------
     Scroll reveals (GSAP + ScrollTrigger, loaded globally by snippets/scripts).
     Opt-in per section via [data-aplus-animate], which the sections omit when the
     theme's "Reduce animations" setting is on. Hidden states are set from JS
     only, so content stays visible if GSAP never loads.
     --------------------------------------------------------------------------- */
  const REVEALS = [
    ['.aplus__header > *, .aplus-compare__heading > *, .aplus-compare__legend', { y: 24 }],
    ['.aplus-image__caption', { y: 32 }],
    ['.aplus-video__chapter', { y: 20 }],
    ['.aplus-tiles__tile', { y: 32 }],
    ['.aplus-carousel__slide', { x: 48 }],
    ['.aplus-text__columns > *', { y: 24 }],
    ['.aplus-compare__picker', { y: 16 }],
    ['.aplus-compare__table', { y: 24 }],
    ['.aplus-fs__header, .aplus-fs__label', { y: 24 }],
    ['.aplus-fs__card, .aplus-fs__group', { y: 32 }],
  ];

  const animateAplus = (root) => {
    const { gsap, ScrollTrigger } = window;
    if (!gsap || !ScrollTrigger || root.aplusMotion) return;
    gsap.registerPlugin(ScrollTrigger);

    root.aplusMotion = gsap.matchMedia();
    root.aplusMotion.add('(prefers-reduced-motion: no-preference)', () => {
      REVEALS.forEach(([selector, from]) => {
        const els = gsap.utils.toArray(root.querySelectorAll(selector));
        if (!els.length) return;
        gsap.set(els, { autoAlpha: 0, ...from });
        ScrollTrigger.batch(els, {
          start: 'top 88%',
          once: true,
          onEnter: (batch) =>
            gsap.to(batch, {
              autoAlpha: 1,
              x: 0,
              y: 0,
              duration: 0.8,
              ease: 'power2.out',
              stagger: 0.08,
              clearProps: 'transform,opacity,visibility',
            }),
        });
      });

      root.querySelectorAll('.aplus-text__rule').forEach((rule) => {
        gsap.from(rule, {
          scaleX: 0,
          transformOrigin: 'left center',
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: { trigger: rule, start: 'top 88%', once: true },
        });
      });
    });
  };

  /* Chapter loop clips: play only while on screen; stay still for reduced motion */
  const initLoops = (scope = document) => {
    const loops = scope.querySelectorAll('video[data-aplus-loop]');
    if (!loops.length) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const observer = still
      ? null
      : new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) target.play().catch(() => {});
            else target.pause();
          });
        });
    loops.forEach((video) => {
      video.muted = true;
      if (still) {
        video.removeAttribute('autoplay');
        video.pause();
      } else {
        observer.observe(video);
      }
    });
  };

  const initAplusMotion = (scope = document) => scope.querySelectorAll('[data-aplus-animate]').forEach(animateAplus);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAplusMotion();
      initLoops();
    });
  } else {
    initAplusMotion();
    initLoops();
  }
  window.addEventListener('load', () => window.ScrollTrigger?.refresh());

  document.addEventListener('shopify:section:load', (event) => {
    initAplusMotion(event.target);
    initLoops(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-aplus-animate]').forEach((root) => root.aplusMotion?.revert());
  });
})();
